import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useCycle } from './CycleContext';
import { differenceInDays, parseISO, addDays, format, subDays } from 'date-fns';

interface NotificationAlert {
  id: string;
  type: 'period_reminder' | 'irregular_cycle' | 'medical_emergency' | 'health_tip' | 'ovulation_reminder';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'critical' | 'success';
  actionRequired: boolean;
  data?: any;
  createdAt: Date;
  isRead: boolean;
  isPersistent: boolean;
}

interface MedicalAlert {
  type: 'heavy_bleeding' | 'severe_pain' | 'irregular_cycle' | 'missed_periods' | 'unusual_symptoms';
  severity: 'warning' | 'critical';
  message: string;
  recommendation: string;
  urgency: 'low' | 'medium' | 'high' | 'urgent';
}

interface NotificationContextType {
  alerts: NotificationAlert[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  addAlert: (alert: Omit<NotificationAlert, 'id' | 'createdAt' | 'isRead'>) => void;
  markAsRead: (id: string) => void;
  clearAlert: (id: string) => void;
  clearAllAlerts: () => void;
  checkForAlerts: () => Promise<void>;
  requestNotificationPermission: () => Promise<boolean>;
  sendBrowserNotification: (title: string, message: string, options?: NotificationOptions) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [alerts, setAlerts] = useState<NotificationAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const { logs, stats } = useCycle();

  const unreadCount = alerts.filter(alert => !alert.isRead).length;

  // Request notification permission
  const requestNotificationPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  };

  // Send browser notification
  const sendBrowserNotification = (title: string, message: string, options?: NotificationOptions) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'threedot-notification',
        requireInteraction: true,
        ...options
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto close after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);
    }
  };

  // Add new alert
  const addAlert = (alert: Omit<NotificationAlert, 'id' | 'createdAt' | 'isRead'>) => {
    const newAlert: NotificationAlert = {
      ...alert,
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      isRead: false
    };

    setAlerts(prev => [newAlert, ...prev]);

    // Send browser notification for critical alerts
    if (alert.severity === 'critical' || alert.severity === 'warning') {
      sendBrowserNotification(alert.title, alert.message, {
        icon: alert.severity === 'critical' ? '🚨' : '⚠️'
      });
    }
  };

  // Mark alert as read
  const markAsRead = (id: string) => {
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === id ? { ...alert, isRead: true } : alert
      )
    );
  };

  // Clear specific alert
  const clearAlert = (id: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  };

  // Clear all alerts
  const clearAllAlerts = () => {
    setAlerts([]);
  };

  // Analyze cycle for irregularities and medical concerns
  const analyzeCycleHealth = (): MedicalAlert[] => {
    const medicalAlerts: MedicalAlert[] = [];

    if (!logs || logs.length === 0) return medicalAlerts;

    // Get period start dates
    const periodStarts = logs
      .filter(log => log.period_start)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Check for irregular cycles
    if (periodStarts.length >= 3) {
      const cycleLengths = [];
      for (let i = 1; i < periodStarts.length; i++) {
        const length = differenceInDays(
          parseISO(periodStarts[i].date),
          parseISO(periodStarts[i - 1].date)
        );
        cycleLengths.push(length);
      }

      const avgCycleLength = cycleLengths.reduce((sum, length) => sum + length, 0) / cycleLengths.length;
      const variance = cycleLengths.reduce((sum, length) => sum + Math.pow(length - avgCycleLength, 2), 0) / cycleLengths.length;
      const standardDeviation = Math.sqrt(variance);

      // Irregular cycle detection
      if (standardDeviation > 7) {
        medicalAlerts.push({
          type: 'irregular_cycle',
          severity: 'warning',
          message: `Your cycle varies significantly (±${standardDeviation.toFixed(1)} days). This could indicate hormonal imbalances or other health conditions.`,
          recommendation: 'Consider tracking lifestyle factors (stress, diet, exercise) and consult a healthcare provider if irregularity persists.',
          urgency: 'medium'
        });
      }

      // Very short cycles (< 21 days)
      const shortCycles = cycleLengths.filter(length => length < 21).length;
      if (shortCycles > 0) {
        medicalAlerts.push({
          type: 'irregular_cycle',
          severity: 'warning',
          message: `You've had ${shortCycles} cycle(s) shorter than 21 days. This could indicate hormonal issues.`,
          recommendation: 'Consult a healthcare provider to rule out conditions like hyperthyroidism or PCOS.',
          urgency: 'medium'
        });
      }

      // Very long cycles (> 35 days)
      const longCycles = cycleLengths.filter(length => length > 35).length;
      if (longCycles > 0) {
        medicalAlerts.push({
          type: 'irregular_cycle',
          severity: 'warning',
          message: `You've had ${longCycles} cycle(s) longer than 35 days. This could indicate PCOS or other hormonal conditions.`,
          recommendation: 'Consider consulting a healthcare provider for evaluation of potential underlying conditions.',
          urgency: 'medium'
        });
      }
    }

    // Check for missed periods (no period in last 45 days)
    if (periodStarts.length > 0) {
      const lastPeriod = parseISO(periodStarts[periodStarts.length - 1].date);
      const daysSinceLastPeriod = differenceInDays(new Date(), lastPeriod);
      
      if (daysSinceLastPeriod > 45) {
        medicalAlerts.push({
          type: 'missed_periods',
          severity: 'critical',
          message: `It's been ${daysSinceLastPeriod} days since your last period. This could indicate pregnancy or other health conditions.`,
          recommendation: 'Take a pregnancy test if sexually active, and consult a healthcare provider if the test is negative.',
          urgency: 'high'
        });
      }
    }

    // Analyze recent logs for concerning symptoms
    const recentLogs = logs.filter(log => 
      differenceInDays(new Date(), parseISO(log.date)) <= 30
    );

    // Check for heavy bleeding patterns
    const heavyFlowDays = recentLogs.filter(log => 
      log.flow_level === 'heavy' || log.flow_level === 'medium_heavy'
    ).length;

    if (heavyFlowDays > 7) {
      medicalAlerts.push({
        type: 'heavy_bleeding',
        severity: 'warning',
        message: `You've logged ${heavyFlowDays} days of heavy flow in the last month. Prolonged heavy bleeding can lead to anemia.`,
        recommendation: 'Monitor your energy levels and consider consulting a healthcare provider if heavy bleeding continues.',
        urgency: 'medium'
      });
    }

    // Check for severe pain patterns
    const severePainDays = recentLogs.filter(log => 
      log.symptoms?.includes('cramps') && log.notes?.toLowerCase().includes('severe')
    ).length;

    if (severePainDays > 3) {
      medicalAlerts.push({
        type: 'severe_pain',
        severity: 'warning',
        message: `You've reported severe pain on ${severePainDays} days this month. Severe menstrual pain could indicate endometriosis or other conditions.`,
        recommendation: 'Consider pain management strategies and consult a healthcare provider for evaluation.',
        urgency: 'medium'
      });
    }

    // Check for unusual symptom combinations
    const concerningSymptoms = recentLogs.filter(log => 
      log.symptoms?.some(symptom => 
        ['severe_pain', 'heavy_bleeding', 'dizziness', 'fainting'].includes(symptom)
      )
    );

    if (concerningSymptoms.length > 2) {
      medicalAlerts.push({
        type: 'unusual_symptoms',
        severity: 'critical',
        message: 'You\'ve reported concerning symptoms that may require immediate medical attention.',
        recommendation: 'Contact your healthcare provider immediately or seek emergency care if symptoms are severe.',
        urgency: 'urgent'
      });
    }

    return medicalAlerts;
  };

  // Check for upcoming period and send reminder
  const checkPeriodReminder = () => {
    if (!stats.nextPeriodPrediction) return;

    const nextPeriodDate = parseISO(stats.nextPeriodPrediction);
    const today = new Date();
    const daysUntilPeriod = differenceInDays(nextPeriodDate, today);

    // Send reminder 1 day before period
    if (daysUntilPeriod === 1) {
      addAlert({
        type: 'period_reminder',
        title: '🩸 Period Reminder',
        message: 'Your period is expected to start tomorrow. Make sure you have supplies ready!',
        severity: 'info',
        actionRequired: false,
        isPersistent: false,
        data: {
          expectedDate: stats.nextPeriodPrediction,
          daysUntil: daysUntilPeriod
        }
      });

      // Send browser notification
      sendBrowserNotification(
        'Period Reminder - THREEDOT',
        'Your period is expected to start tomorrow. Make sure you have supplies ready!',
        { icon: '🩸' }
      );
    }

    // Send reminder on the day
    if (daysUntilPeriod === 0) {
      addAlert({
        type: 'period_reminder',
        title: '🩸 Period Day',
        message: 'Your period is expected to start today. Don\'t forget to log it when it begins!',
        severity: 'info',
        actionRequired: true,
        isPersistent: true,
        data: {
          expectedDate: stats.nextPeriodPrediction,
          daysUntil: daysUntilPeriod
        }
      });
    }
  };

  // Check for ovulation reminder
  const checkOvulationReminder = () => {
    if (!stats.lastPeriodDate) return;

    const lastPeriod = parseISO(stats.lastPeriodDate);
    const daysSinceLastPeriod = differenceInDays(new Date(), lastPeriod);
    
    // Ovulation typically occurs 12-16 days before next period
    // For a 28-day cycle, that's around day 12-16
    const ovulationDay = Math.round(stats.averageCycleLength / 2);
    
    if (daysSinceLastPeriod === ovulationDay - 1) {
      addAlert({
        type: 'ovulation_reminder',
        title: '🥚 Ovulation Window',
        message: 'You\'re entering your fertile window. This is the best time for conception if you\'re trying to get pregnant.',
        severity: 'info',
        actionRequired: false,
        isPersistent: false,
        data: {
          ovulationDay: daysSinceLastPeriod + 1,
          fertileWindow: `Day ${daysSinceLastPeriod} - ${daysSinceLastPeriod + 3}`
        }
      });
    }
  };

  // Main function to check for all alerts
  const checkForAlerts = async () => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      setError(null);

      // Check for medical alerts
      const medicalAlerts = analyzeCycleHealth();
      
      medicalAlerts.forEach(alert => {
        addAlert({
          type: alert.type as any,
          title: getAlertTitle(alert.type, alert.severity),
          message: alert.message,
          severity: alert.severity === 'critical' ? 'critical' : 'warning',
          actionRequired: alert.urgency === 'urgent' || alert.urgency === 'high',
          isPersistent: alert.urgency === 'urgent',
          data: {
            recommendation: alert.recommendation,
            urgency: alert.urgency
          }
        });
      });

      // Check for period reminders
      checkPeriodReminder();

      // Check for ovulation reminders
      checkOvulationReminder();

    } catch (err: any) {
      setError(err.message || 'Failed to check for alerts');
    } finally {
      setLoading(false);
    }
  };

  // Get appropriate alert title based on type and severity
  const getAlertTitle = (type: string, severity: string): string => {
    const titles = {
      irregular_cycle: severity === 'critical' ? '🚨 Irregular Cycle Alert' : '⚠️ Cycle Irregularity',
      heavy_bleeding: severity === 'critical' ? '🚨 Heavy Bleeding Alert' : '⚠️ Heavy Flow Notice',
      severe_pain: severity === 'critical' ? '🚨 Severe Pain Alert' : '⚠️ Pain Management',
      missed_periods: '🚨 Missed Period Alert',
      unusual_symptoms: '🚨 Health Alert'
    };
    
    return titles[type as keyof typeof titles] || '⚠️ Health Notice';
  };

  // Auto-check for alerts every hour
  useEffect(() => {
    if (isAuthenticated && logs.length > 0) {
      checkForAlerts();
      
      // Set up interval to check every hour
      const interval = setInterval(checkForAlerts, 60 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, logs, stats]);

  // Request notification permission on mount
  useEffect(() => {
    if (isAuthenticated) {
      requestNotificationPermission();
    }
  }, [isAuthenticated]);

  // Clear old alerts (older than 7 days) except persistent ones
  useEffect(() => {
    const sevenDaysAgo = subDays(new Date(), 7);
    setAlerts(prev => 
      prev.filter(alert => 
        alert.isPersistent || alert.createdAt > sevenDaysAgo
      )
    );
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        alerts,
        unreadCount,
        loading,
        error,
        addAlert,
        markAsRead,
        clearAlert,
        clearAllAlerts,
        checkForAlerts,
        requestNotificationPermission,
        sendBrowserNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};